import moment from 'moment';
import currency from 'currency.js';
import { decamelize } from 'humps';
import store from '@/store';
import i18n from '@/libs/i18n';

export const caclulateOffset = (page, limit) => (page - 1) * limit;

export const calculateDatesByPeriod = period => {
  if (period === 'today') {
    return [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')];
  }
  if (period === 'yesterday') {
    return [
      moment().subtract(1, 'day').format('YYYY-MM-DD'),
      moment().subtract(1, 'day').format('YYYY-MM-DD')
    ];
  }
  if (period === 'thisWeek') {
    return [
      moment().startOf('isoWeek').format('YYYY-MM-DD'),
      moment().endOf('isoWeek').format('YYYY-MM-DD')
    ];
  }
  if (period === 'lastWeek') {
    return [
      moment().subtract(1, 'weeks').startOf('isoWeek').format('YYYY-MM-DD'),
      moment().subtract(1, 'weeks').endOf('isoWeek').format('YYYY-MM-DD')
    ];
  }
  if (period === 'thisMonth') {
    return [
      moment().startOf('month').format('YYYY-MM-DD'),
      moment().endOf('month').format('YYYY-MM-DD')
    ];
  }
  if (period === 'lastMonth') {
    return [
      moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'),
      moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD')
    ];
  }

  return [];
};
export const currencyFormat = (value, currencyCode) => {
  if (typeof value === 'undefined') {
    return '';
  }
  const val = value;
  const symbol = '';

  if (currencyCode === 840) {
    const USD = value => currency(value);
    return USD(value).format();
  }
  if (currencyCode === 116) {
    const RIEL = value => currency(value, { precision: 2, symbol: '៛' });
    return RIEL(value).format();
  }

  return currency(value);
};
export const prepareSearchParams = (payload, limit) => {
  const params = {
    ...payload,
    limit: limit || store.state.pagination.perPage || 10,
    page: payload?.page || 1,
    order: payload?.order || 'createdAt',
    sort: payload?.sort || 'desc',
    archived: payload?.archived ? 1 : 0
  };

  return params;
};

export const makeid = length => {
  let result = '';
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const generateTextColor = bgColor => {
  // Get background color hex value. '#' is optional.
  let hex = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;

  // Convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // By this point, it should be 6 characters
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Return light or dark class based on contrast calculation
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? 'text-dark' : 'text-light';
};

export const generateVariantColor = bgColor => {
  // Get background color hex value. '#' is optional.
  let hex = bgColor.charAt(0) === '#' ? bgColor.substring(1, 7) : bgColor;

  // Convert 3-digit hex to 6-digits.
  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  // By this point, it should be 6 characters
  if (hex.length !== 6) {
    throw new Error('Invalid HEX color.');
  }

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);

  // Return light or dark class based on contrast calculation
  return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? 'dark' : 'light';
};

export const hexToRgbA = hex => {
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split('');
    if (c.length == 3) {
      c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    }
    c = `0x${c.join('')}`;
    return `rgba(${[(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')},0.12)`;
  }
  throw new Error('Bad Hex');
};

export const rgbaToHex = rgba => {
  try {
    const colors = rgba.replace(/[rgba()]/g, '').split(',');
    const [r, g, b, a] = colors.map(num => parseFloat(num.trim()));

    const toHex = n => Math.round(n).toString(16).padStart(2, '0');
    const alpha = toHex(a * 255);

    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha}`;
  } catch (error) {
    console.error('Invalid RGBA format');
    return null;
  }
};

export const mapValidationMessage = message => {
  const error = {};
  for (const key in message) {
    if (message.hasOwnProperty(key)) {
      error[key] = message[key].map(element =>
        i18n.t(element, { _field_: i18n.t(`field.${key}`) })
      );
    }
  }

  return error;
};

export const mapAbility = (data, newAbility, initialAbility) => {
  let isConversationHeader = false;
  let isConfigurationHeader = false;
  let isWorkingHeader = false;
  const conversationPermission = [
    'chat',
    'call',
    'ticket',
    'report-conversation-overview',
    'report-call-center-overview',
    'report-customer-satisfaction',
    'report-call-satisfaction',
    'report-team-performance',
    'summary-conversation'
  ];
  const workingPermission = ['service', 'employee-performance'];
  const configurationPermission = [
    'chat-settings',
    'call-center',
    'service-setting',
    'merchant-integration',
    'modify-business-info',
    'manage-project',
    'manage-department',
    'manage-users',
    'manage-role'
  ];

  if (data.isReadonly) {
    data.ability.forEach(x => {
      x.action = 'read';
      return x;
    });
  }

  for (const ability of data.ability) {
    if (isConversationHeader && isConfigurationHeader && isWorkingHeader) {
      break;
    }

    if (!isConversationHeader) {
      const conversationIndex = conversationPermission.findIndex(
        element => element == ability.subject
      );

      if (conversationIndex != -1) {
        isConversationHeader = true;
      }
    }

    if (!isConfigurationHeader) {
      const configurationIndex = configurationPermission.findIndex(
        element => element == ability.subject
      );

      if (configurationIndex != -1) {
        isConfigurationHeader = true;
      }
    }

    if (!isWorkingHeader) {
      const workingIndex = workingPermission.findIndex(
        element => element == ability.subject
      );

      if (workingIndex != -1) {
        isWorkingHeader = true;
      }
    }
  }
  newAbility = [...data.ability, ...initialAbility];
  if (isConversationHeader) {
    newAbility = [
      ...newAbility,
      { subject: 'conversation-header', action: 'manage' }
    ];
  }
  if (isConfigurationHeader) {
    newAbility = [
      ...newAbility,
      { subject: 'configuration-header', action: 'manage' }
    ];
  }
  if (isWorkingHeader) {
    newAbility = [
      ...newAbility,
      { subject: 'working-header', action: 'manage' }
    ];
  }
  return newAbility;
};

export const formatDate = (value, format = 'DD MMMM YYYY h:mm A') => {
  if (!value) {
    return '';
  }

  // if (store.state?.profile?.origin) {
  //   return moment(value)
  //     .locale(i18n.locale)
  //     .tz(store.state.profile.origin)
  //     .format(format);
  // }

  return moment(value).locale(i18n.locale).format(format);
};

export const getCountryByLanguageCode = (source, languagesCode) => {
  if (!source) {
    return {};
  }

  return source.find(val => val.language == languagesCode);
};

export const secondsToDHMS = seconds => {
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  let result = '';

  if (days > 0) {
    result += `${days} ${i18n.tc('field.day', days)} `;
  }
  if (hours > 0) {
    result += `${hours} ${i18n.tc('field.hour', hours)} `;
  }
  if (minutes > 0) {
    result += `${minutes} ${i18n.tc('field.minute', minutes)} `;
  }
  if (remainingSeconds > 0) {
    result += `${remainingSeconds} ${i18n.tc(
      'field.second',
      remainingSeconds
    )} `;
  }

  return result.trim();
};

export const memoize = fn => {
  const cache = new Map();
  return (...args) => {
    const key = JSON.stringify(args);
    if (cache.has(key)) {
      return cache.get(key);
    }
    const result = fn(...args);
    cache.set(key, result);
    return result;
  };
};

// Color contrast utility functions
export const generateContrastColor = memoize((color, alpha) => {
  // Parse different color formats
  let r,
    g,
    b,
    a = 1;

  if (color.startsWith('#')) {
    // Handle hex colors
    const hex = color.replace('#', '');
    if (hex.length === 3) {
      // Convert short hex to full hex
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else if (hex.length === 6) {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    } else if (hex.length === 8) {
      // Handle hex with alpha
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
      a = parseInt(hex.substring(6, 8), 16) / 255; // Convert alpha to 0-1 range
    }
  } else if (color.startsWith('rgb')) {
    // Handle rgb and rgba
    const values = color.match(/[\d.]+/g);
    r = parseInt(values[0]);
    g = parseInt(values[1]);
    b = parseInt(values[2]);
    if (values.length === 4) {
      a = parseFloat(values[3]);
    }
  } else {
    throw new Error('Invalid color format');
  }

  // Blend with white background based on alpha
  if (a < 1) {
    r = Math.round(r * a + 255 * (1 - a));
    g = Math.round(g * a + 255 * (1 - a));
    b = Math.round(b * a + 255 * (1 - a));
  }

  // Calculate relative luminance using WCAG formula
  const [lr, lg, lb] = [r, g, b].map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });

  const luminance = 0.2126 * lr + 0.7152 * lg + 0.0722 * lb;

  if (alpha) {
    return luminance > 0.5
      ? `rgba(0,0,0,${alpha})`
      : `rgba(255,255,255,${alpha})`;
  }

  return luminance > 0.5 ? '#000000' : '#FFFFFF';
});

export function uniqueId() {
  return 'id-' + Date.now() + '-' + Math.floor(Math.random() * 1000);
}
